<script>
	export default {
		name: 'FileUploadBase',
		props: {
			accept: String,
			title: String,
			loading: Boolean,
			secondaryAction: String,
			primaryAction: String,
			flat: { type: Boolean, default: false },
			hideTitle: Boolean,
			hideFileAccept: Boolean,
			multiple: { type: Boolean, default: false }
		},
		data() {
			return {
				files: [],
				dropClasses: { 'drop-active': false }
			};
		},
		computed: {
			fileAccept() {
				return this.accept || '*';
			},
			acceptInfoMessage() {
				let friendlyType = '';
				switch (this.accept) {
					case 'application/pdf':
						friendlyType = 'PDF';
						break;
					case 'text/csv':
						friendlyType = 'CSV';
						break;
					case 'image/*':
						friendlyType = 'PNG, JPG, JPEG, SVG';
						break;
				}
				return `Please ensure file is in ${friendlyType} format.`;
			}
		},
		methods: {
			onPrimaryAction() {
				let formData = new FormData();
				// Single file: use 'file'
				if (this.files.length === 1) {
					formData.append('file', this.files[0]);
				} else {
					// Multiple files: use 'file0', 'file1', etc.
					this.files.forEach((file, i) => {
						formData.append('file' + i, file);
					});
				}
				this.$emit('onPrimaryAction', formData, this.files);
			},
			onSecondaryAction() {
				this.$emit('onSecondaryAction');
			},
			handleFileUpload() {
				const inputFiles = Array.from(this.$refs.file.files);
				this.addFiles(inputFiles);
			},
			handleDragover(ev) {
				ev.dataTransfer.dropEffect = 'copy';
				this.dropClasses['drop-active'] = true;
			},
			handleDragleave() {
				this.dropClasses['drop-active'] = false;
			},
			handleDrop(ev) {
				this.dropClasses['drop-active'] = false;
				const droppedFiles = Array.from(ev.dataTransfer.files);

				if (!this.multiple && droppedFiles.length > 1) {
					this.$toast.error('Only one file can be uploaded.');
					return;
				}

				this.addFiles(droppedFiles);
			},
			addFiles(newFiles) {
				if (!this.multiple) {
					this.files = [newFiles[0]];
				} else {
					this.files.push(...newFiles);
				}
				this.$emit('onFileChange', this.files);
				this.$emit('droppedFile', newFiles);
			},
			handleFileDelete(index) {
				this.files.splice(index, 1);
				this.$refs.file.value = '';
				this.$emit('onFileChange', this.files);
			},
			getFileSize(bytes) {
				return bytes < 1000000 ? `${Math.round(bytes / 1000)}kb` : `${Math.round(bytes / 1000000)}mb`;
			},
			isImage(file) {
				return file.type.includes('image/');
			},
			getImageSrc(file) {
				return URL.createObjectURL(file);
			},
			setFiles(files) {
				if (this.$refs.fileUploader) {
					this.$refs.fileUploader.files = Array.isArray(files) ? files : [files];
				}
			}
		}
	};
</script>

<template>
	<v-card :flat="flat">
		<v-toolbar flat dense v-if="!hideTitle">
			<v-toolbar-title class="subheading">{{ title }}</v-toolbar-title>
			<v-spacer></v-spacer>
		</v-toolbar>

		<form enctype="multipart/form-data" novalidate>
			<v-card-text>
				<v-alert v-if="!hideFileAccept && fileAccept !== '*'" class="text-xs-left mb-3" :value="true" color="info" icon="info" outline>
					{{ acceptInfoMessage }}
				</v-alert>

				<div v-if="!multiple && files.length === 1 && isImage(files[0])" class="image-preview">
					<img :src="getImageSrc(files[0])" :alt="files[0].name" />
				</div>

				<div v-show="multiple || files.length === 0" id="dropZone" @dragover.prevent="handleDragover" @dragleave.prevent="handleDragleave" @drop.prevent="handleDrop" :class="dropClasses">
					<v-icon>backup</v-icon>
					<div>
						<span>Drag & Drop or</span>
						<a class="blue--text" @click.prevent="$refs.file.click()">
							{{ multiple ? (files.length > 0 ? 'Add More Files' : 'Choose Files') : 'Choose File' }}
						</a>
						<span>to upload.</span>
					</div>
					<input id="file" ref="file" type="file" hidden :accept="fileAccept" :multiple="multiple" @change="handleFileUpload" />
				</div>

				<v-list v-if="files.length > 0" class="items mt-3">
					<v-list-tile v-for="(file, index) in files" :key="index">
						<v-list-tile-action>
							<div v-if="isImage(file)" class="preview-thumb">
								<img :src="getImageSrc(file)" :alt="file.name" />
							</div>
							<v-icon v-else>mdi-file-document-outline</v-icon>
						</v-list-tile-action>

						<v-list-tile-content>
							<v-list-tile-title>{{ file.name }}</v-list-tile-title>
							<v-list-tile-sub-title>{{ getFileSize(file.size) }}</v-list-tile-sub-title>
						</v-list-tile-content>

						<v-list-tile-action>
							<v-tooltip bottom>
								<v-btn slot="activator" icon flat @click.prevent="handleFileDelete(index)">
									<v-icon>mdi-delete</v-icon>
								</v-btn>
								<span>Delete File</span>
							</v-tooltip>
						</v-list-tile-action>
					</v-list-tile>
				</v-list>
			</v-card-text>

			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn v-if="secondaryAction" flat @click.native="onSecondaryAction">{{ secondaryAction }}</v-btn>
				<v-btn v-if="primaryAction" color="blue darken-1" flat @click.prevent="onPrimaryAction" :loading="loading" :disabled="loading || files.length === 0">
					{{ primaryAction }}
				</v-btn>
			</v-card-actions>
		</form>
	</v-card>
</template>

<style scoped lang="scss">
	#dropZone {
		border-radius: 5px;
		width: 100%;
		border: 1.5px dashed #9e9e9e;
		padding: 6rem 2rem;
		background: #f5f4f4;
		text-align: center;
		margin-bottom: 1rem;
	}
	#dropZone.drop-active {
		border: 1.5px solid #1976d2;
	}
	.image-preview img {
		width: 100%;
	}
	.preview-thumb {
		width: 40px;
		height: 40px;
		border-radius: 4px;
		overflow: hidden;
		border: 1px solid #ccc;
		display: flex;
		align-items: center;
		justify-content: center;
		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}
</style>
